import { useState } from "react";

import { DATE_FORMAT } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { emitNotification } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";
import moment from "moment-timezone";

import {
    postControlResultsExtendDueDate,
    getGetControlResultsInfiniteQueryKey,
} from "api/generated/control-results/control-results";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import { ControlActivityTableRow } from "./types";
import {
    CALENDAR_DAY_FORMAT,
    CalendarDay,
    computeExtendDueDateToolState,
    computeSelectionDueDates,
    newDueDateInstant,
} from "./utils/extendDueDate";

interface UseControlActivityExtendDueDateUtilsProps {
    selectedRows: Row<ControlActivityTableRow>[];
    deselectAll: () => void;
}

export function useControlActivityExtendDueDateUtils({
    selectedRows,
    deselectAll,
}: UseControlActivityExtendDueDateUtilsProps) {
    const { t } = useTranslation();
    const timezoneName = useCurrentUserTimezone();
    const [extendDueDateModalOpen, setExtendDueDateModalOpen] = useState(false);
    const [isExtendingDueDate, setIsExtendingDueDate] = useState(false);

    const rows = selectedRows.map((row) => row.original);
    const extendDueDateToolState = computeExtendDueDateToolState(t, rows);
    const selectionDueDates = computeSelectionDueDates(rows);

    const openExtendDueDateModal = () => setExtendDueDateModalOpen(true);
    const closeExtendDueDateModal = () => setExtendDueDateModalOpen(false);

    const handleExtendDueDate = async (
        newDueDay: CalendarDay,
        comment: string
    ) => {
        const controlResultIds = rows.map((row) => row.activityId);
        setIsExtendingDueDate(true);
        try {
            await postControlResultsExtendDueDate({
                controlResultIds,
                // The picked calendar day becomes 23:59 in the acting user's
                // timezone, applied to the whole selection.
                newDueDate: newDueDateInstant(newDueDay, timezoneName),
                ...(comment !== "" && { comment }),
            });
            // The rows' due dates changed, so refetch.
            await queryClient.invalidateQueries({
                queryKey: getGetControlResultsInfiniteQueryKey(),
            });
            emitNotification(
                "success",
                t("ControlActivitiesList_ExtendDueDate_Success", {
                    count: controlResultIds.length,
                    date: moment(newDueDay, CALENDAR_DAY_FORMAT).format(
                        DATE_FORMAT
                    ),
                })
            );
            deselectAll();
            closeExtendDueDateModal();
        } catch {
            // The selection and the modal are kept so the user can retry.
            emitNotification(
                "error",
                t("ControlActivitiesList_ExtendDueDate_Failed")
            );
        } finally {
            setIsExtendingDueDate(false);
        }
    };

    return {
        extendDueDateToolState,
        selectionDueDates,
        extendDueDateModalOpen,
        openExtendDueDateModal,
        closeExtendDueDateModal,
        handleExtendDueDate,
        isExtendingDueDate,
    };
}
